import React, { useMemo } from 'react';
// State
import { useDispatch, useSelector } from 'react-redux';
// Selectors
import {
  selectPagesInWorkspace, selectPagesInWorkspaceByPreEvent,
  selectEpisodesInWorkspace, selectEpisodesInWorkspaceByPreEvent
} from 'app/store/page/page.selectors';
import { selectWorkspaceType } from 'app/store/ui/uiSelectors';
import { Box } from '@mui/material';
// Components
import { Loader, Message } from 'app/components/Utilities';
// 
import WorkspaceList from './WorkspaceList';
import WorkspaceStapleFab from './WorkspaceStapleFab';
import WorkspaceScroll from './WorkspaceScroll';
// 
import { useParams } from 'react-router-dom';
import Mixpanel from 'app/services/Mixpanel.service';
import MixpanelTracks from 'app/types/MixpanelTracks';

import PageActions from 'app/store/page/page.actions';

import FilesSidebar from 'app/layouts/DocumentsSidebar';
// i18next
import { useTranslation } from 'react-i18next';

import WorkspaceInfoText from './WorkspaceInfoText';
import PagesToolbar from 'app/components/PagesToolbar';

const WorkspacePage = () => {
  const { t } = useTranslation();

  const listRef = React.useRef<any>();

  // Dispatch
  const dispatch = useDispatch();

  const workspaceType = useSelector(selectWorkspaceType);
  const pagesInWorkspace = useSelector(selectPagesInWorkspace);
  const episodesInWorkspace = useSelector(selectEpisodesInWorkspace);

  const pagesPreEvent = useSelector((state:any) => selectPagesInWorkspaceByPreEvent(state, { preEvent: true }));
  const pagesPostEvent = useSelector((state:any) => selectPagesInWorkspaceByPreEvent(state, { preEvent: false }));

  const episodesPreEvent = useSelector((state:any) => selectEpisodesInWorkspaceByPreEvent(state, { preEvent: true }));
  const episodesPostEvent = useSelector((state:any) => selectEpisodesInWorkspaceByPreEvent(state, { preEvent: false }));

  const { caseId } = useParams<{ caseId:string }>();

  React.useEffect(() => {
    Mixpanel.track(MixpanelTracks.CaseWorkspaceView, { id: Number(caseId) });

    return () => {
      dispatch(PageActions.setInitialField('filter'));
    }
    // eslint-disable-next-line
  }, []);

  const allPages = useMemo(() => {
    return [...(pagesPreEvent || []), ...(pagesPostEvent || [])];
  }, [pagesPreEvent, pagesPostEvent]);

  const pages = useMemo(() => {
    const result:any = [];
    if ( pagesPreEvent && pagesPreEvent.length ){
      result.push({ type: 'placeholder', label: 'Pre-event' });
      for ( let i = 0; i < pagesPreEvent.length; i++ ){
        result.push({...pagesPreEvent[i], type: 'page', index: i });
      }
    }
    if ( pagesPostEvent && pagesPostEvent.length ){
      const index = pagesPreEvent ? pagesPreEvent.length : 0;
      result.push({ type: 'placeholder', label: 'Post-event' });
      for ( let i = 0; i < pagesPostEvent.length; i++ ){
        result.push({...pagesPostEvent[i], type: 'page', index: index + i });
      }
    }
    return result;
    // eslint-disable-next-line
  }, [pagesPreEvent, pagesPostEvent]);

  const episodes = useMemo(() => {
    const result:any = [];
    if ( episodesPreEvent && episodesPreEvent.length ){
      result.push({ type: 'placeholder', label: 'Pre-event' });
      for ( let i = 0; i < episodesPreEvent.length; i++ ){
        result.push({...episodesPreEvent[i], type: 'page', index: i });
      }
    }
    if ( episodesPostEvent && episodesPostEvent.length ){
      const index = pagesPreEvent ? pagesPreEvent.length : 0;
      result.push({ type: 'placeholder', label: 'Post-event' });
      for ( let i = 0; i < episodesPostEvent.length; i++ ){
        result.push({...episodesPostEvent[i], type: 'page', index: index + i });
      }
    }
    return result;
    // eslint-disable-next-line
  }, [episodesPreEvent, episodesPostEvent]);

  const renderPages = () => {
    if ( workspaceType === 'episodes' ) return null;
    if ( !pagesInWorkspace ) return <Box sx={{ width: '100%' }}><Loader /></Box>;
    if ( !pagesInWorkspace.length ) return <Message text={t('pages.workspace.noPages')} />;
    return <WorkspaceList pages={pages} listRef={listRef} />;
  } 

  const renderEpisodes = () => {
    if ( workspaceType === 'pages' ) return null;
    if ( !episodesInWorkspace ) return <Box sx={{ width: '100%' }}><Loader /></Box>;
    if ( !episodesInWorkspace.length ) return <Message text={t('pages.workspace.noEpisodes')} />;
    return <WorkspaceList pages={episodes} listRef={listRef} />;
  }

  return (
    <Box sx={{
      flexGrow: 1,
      display: 'flex',
      overflow: 'hidden'
    }}>
      <FilesSidebar />
      <PagesToolbar pages={allPages} toolType="workspace" listRef={listRef}>
        <Box sx={{ flexGrow: 1, height: '100%', display: 'flex' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <PagesToolbar.Bar>
              <PagesToolbar.Actions>
                <WorkspaceInfoText />
              </PagesToolbar.Actions>
            </PagesToolbar.Bar>
            <PagesToolbar.Filters />
            <PagesToolbar.FiltersResults />
            <Box sx={{ flexGrow: 1, display: 'flex', position: 'relative' }}>
              {renderPages()}
              {renderEpisodes()}
              <Box sx={{
                position: 'absolute',
                right: 0, bottom: 0,
                m: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 2
              }}>
                <WorkspaceScroll listRef={listRef} />
                <WorkspaceStapleFab />
              </Box>
            </Box>
          </Box>
          <PagesToolbar.FiltersSidebar />
        </Box>
      </PagesToolbar>
    </Box>
  )
}

export default WorkspacePage;
